<template>
  <div class="grid-universe">
    <filterBar
      @updateFilters="updateFilters(...arguments)"
      :client="selectedClient"
      :fiduciary="selectedFiduciary"
      @updateProductFunds="updateProduct"
    />
    <div class="actions-bar">
      <div class="box left">
        <div class="rowCount" v-if="!isLoading">{{ totalRowsNumber }} total funds</div>
        <div class="rowCount" v-else>Loading...</div>
      </div>
      <div class="box right">
        <contextual-menu
          class="import-xls-files-menu"
          position="bottom-left"
          text="Import..."
          :options="formatActionChoices()"
        >
          <BaseGridIcon
            slot="clickTrigger"
            data-mds-popover="mds-popover"
            aria-haspopup="true"
            aria-expanded="true"
            aria-controls="mds-popover--8809"
            role="button"
            class="mds-icon"
            aria-hidden="true"
            icon="import"
            tooltipMessage="Import"
            @showToolTip="showToolTipMessage"
            @hideToolTip="onHideTooltip"
          />
        </contextual-menu>

        <button
          id="export-view"
          class="mds-button mds-button--icon-only exportButton"
          type="button"
          :disabled="isExportDisabled"
          @click="openExportModal"
          @mouseover="onShowTooltip($event, ['Export'], 'bottom-right', 'small')"
          @mouseleave="onHideTooltip()"
        >
          <svg class="mds-icon mds-button__icon" aria-hidden="true">
            <use
              xmlns:xlink="http://www.w3.org/1999/xlink"
              xlink:href="static/mds/icons/mds.svg#share"
            ></use>
            <title>Export</title>
          </svg>
        </button>

        <button
          id="custom-fund-view"
          class="mds-button mds-button--icon-only exportButton"
          type="button"
          :disabled="isExportDisabled"
          @click="navigateToCustomFunds"
          @mouseover="onShowTooltip($event, ['Create Custom Funds'], 'bottom-right', 'small')"
          @mouseleave="onHideTooltip()"
        >
          <svg class="mds-icon mds-button__icon" aria-hidden="true">
            <use
              xmlns:xlink="http://www.w3.org/1999/xlink"
              xlink:href="static/mds/icons/mds.svg#plus"
            ></use>
            <title>Create Custom Funds</title>
          </svg>
        </button>
      </div>
    </div>
    <div class="grid-wrapper" v-on:scroll.passive="handleScroll">
      <GridUniverseGrid
        :scrolled="scrolled"
        :filters="currentFilters"
        @showToolTip="onShowTooltip"
        @hideToolTip="onHideTooltip"
      />
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import { get } from 'lodash';

import { EventBus } from '@/utils/event-bus';

import BaseGridIcon from '@/components/atoms/BaseGridIcon';
import ContextualMenu from '@/components/molecules/ContextualMenu';
import FilterBar from '@/components/organisms/FilterBar';
import GridUniverseGrid from '@/components/organisms/GridUniverseGrid';

export default {
  name: 'grid-universe',
  computed: {
    ...mapState({
      policies: (state) => state.user.policies,
      selectedFiduciary: (state) => state.user.fiduciary.id,
      selectedClient: (state) => state.grid.client.selected,
      totalRowsNumber: (state) => state.grid.totalRows,
      selectedTab: (state) => state.grid.tab,
      isLoading: (state) => state.system.isLoading,
    }),
    currentViewName() {
      return this.selectedClient > 0 ? 'one-client' : 'all-clients';
    },
    localPolicyOptions() {
      // Tabs are applied to 1 Client View
      if (this.currentViewName === 'all-clients') {
        return get(this.policies, `grid.views.${this.currentViewName}`);
      }
      return get(this.policies, `grid.views.${this.currentViewName}.tabs.${this.selectedTab}`);
    },
    isExportDisabled() {
      return !get(this.localPolicyOptions, 'options.export');
    },
  },
  components: {
    BaseGridIcon,
    GridUniverseGrid,
    FilterBar,
    ContextualMenu,
  },
  data() {
    return {
      currentFilters: {
        assets: [],
        categories: [],
        status: [],
      },
      scrolled: false,
      allStatusMode: false,
    };
  },
  created() {
    this.$store.dispatch('setTotalRows', 0);
    EventBus.$emit('search-clear');
  },
  methods: {
    showToolTipMessage(event, message) {
      this.onShowTooltip(event, message, 'bottom-right', 'small');
    },
    onShowTooltip(event, status, orientation, size) {
      this.$emit('showTooltip', event, status, orientation, size);
    },
    onHideTooltip(event) {
      this.$emit('hideTooltip', event);
    },
    formatActionChoices() {
      const options = [
        {
          id: 'gu-import-lineups',
          disabled: false,
          text: 'Import Lineups',
          link: '/import-lineups',
        },
        {
          id: 'gu-import-approvals',
          disabled: false,
          text: 'Import Approvals',
          link: '/import-approvals',
        },
      ];
      return options;
    },
    updateFilters(filters, allStatus) {
      this.currentFilters.assets = filters.assets;
      this.currentFilters.categories = filters.categories;
      this.currentFilters.status = filters.status;
      this.allStatusMode = allStatus;
    },
    updateProduct(product, productName, productCode) {
      this.$store.dispatch('setProduct', {
        code: productCode,
        listVisible: false,
        name: productName,
        selected: product,
      });
    },
    openExportModal() {
      this.$store.dispatch('openModal', {
        title: 'Export',
        component: 'export-data',
        data: this.currentFilters,
      });
    },
    handleScroll(event) {
      const footerHeight = 100; // Loading image height + page footer height
      this.scrolled = event.target.offsetHeight + event.target.scrollTop
        >= event.target.scrollHeight - event.target.clientHeight - footerHeight;
    },
    navigateToCustomFunds() {
      this.onHideTooltip();
      this.$router.push({ name: 'createCustomFunds' });
    },
  },
};
</script>

<style lang="scss">
.grid-universe {
  flex: 1 1 100%;
  display: flex;
  flex-direction: column;
  position: relative;

  .grid-wrapper {
    height: calc(100% - 61px);
    overflow-y: auto;
    width: 100%;
  }

  @media screen and (max-height: 624px) {
    .single-list, .multiple-list {
      max-height: 250px;
    }
  }
}

.actions-bar {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  padding-bottom: $mds-space-2-x;
  font-family: $mds-font-family-sans-serif;
  font-size: $mds-font-size-s;
  margin-top: $mds-space-2-x;

  .rowCount {
    margin: 0 $mds-space-4-x;
    flex-grow: 1;
    font-weight: $mds-font-weight-light;
  }
  .box.right {
    margin-right: $mds-space-inset-4-x;
  }
  button {
    margin-left: $mds-space-inset-2-x;
  }
  .import-xls-files-menu {
    display: inline-block;
  }
  svg.mds-icon.mds-icon__import {
    height: 26px;
    width: 26px;
  }
}
</style>
